// Optional import filters the loan officer chooses on the import dialog.
//
// POINT offers the same choice: leave out tradelines with a zero balance and/or
// a zero monthly payment, so only debts that actually affect the file land on
// the Debt page and in the DTI. This is a CHOICE, never automatic — a deferred
// student loan reports $0/$0 but some investors still require a percentage of
// the balance in the DTI, so the LO decides.

/** True if a debt should be dropped given the chosen options. */
function shouldDropDebt(account, { ignoreZeroBalance, ignoreZeroPayment }) {
  const payment = parseFloat(account.monthly_payment) || 0;
  const balance = parseFloat(account.current_balance) || 0;
  if (ignoreZeroPayment && payment === 0) return true;
  if (ignoreZeroBalance && balance === 0) return true;
  return false;
}

/** How many debts the chosen options would remove (for the dialog count). */
export function countFilteredDebts(document, options) {
  if (!document || !document.accountSummaries) return 0;
  if (!options || (!options.ignoreZeroBalance && !options.ignoreZeroPayment)) {
    return 0;
  }
  return Object.values(document.accountSummaries).filter(a =>
    shouldDropDebt(a, options)
  ).length;
}

/**
 * Return a copy of the document with the filtered-out debts removed from both
 * accountSummaries and every loan's account_dispositions. No options set =>
 * the document is returned unchanged.
 */
export function applyDebtImportFilter(document, options) {
  if (!document || !document.accountSummaries) return document;
  if (!options || (!options.ignoreZeroBalance && !options.ignoreZeroPayment)) {
    return document;
  }

  const removedIds = new Set();
  const accountSummaries = {};
  Object.entries(document.accountSummaries).forEach(([id, account]) => {
    if (shouldDropDebt(account, options)) {
      removedIds.add(id);
    } else {
      accountSummaries[id] = account;
    }
  });

  if (removedIds.size === 0) return document;

  const loans = (document.loans || []).map(loan => {
    if (!loan.account_dispositions) return loan;
    const account_dispositions = {};
    Object.entries(loan.account_dispositions).forEach(([id, disp]) => {
      if (!removedIds.has(id)) account_dispositions[id] = disp;
    });
    return { ...loan, account_dispositions };
  });

  return { ...document, accountSummaries, loans };
}
